import { useRouter } from 'next/router';
import Link from 'next/link';
import clsx from 'clsx';

import styles from './Pagination.module.scss';

const PaginationItem = ({
	isActive = false,
	disabled = false,
	href,
	asPath,
	shallow,
	children,
	...props
}) => {
	if (isActive || disabled) {
		return (
			<li>
				<span
					className={clsx(
						styles['pagination-link'],
						(props.prev || props.next) && styles['pagination-link-prev-next'],
						props.prev && styles['pagination-link-prev'],
						props.next && styles['pagination-link-next'],
						isActive && styles['pagination-link-active'],
						disabled && styles['pagination-link-disabled'],
					)}
				>
					{children}
				</span>
			</li>
		);
	}
	return (
		<li>
			<Link href={href} as={asPath} passHref shallow={shallow}>
				<a
					className={clsx(
						styles['pagination-link'],
						(props.prev || props.next) && styles['pagination-link-prev-next'],
						props.prev && styles['pagination-link-prev'],
						props.next && styles['pagination-link-next'],
					)}
				>
					{children}
				</a>
			</Link>
		</li>
	);
};

const PaginationEllipsis = props => {
	return (
		<li>
			<span
				className={clsx(
					styles['pagination-link'],
					(props.prev || props.next) && styles['pagination-link-prev-next'],
					props.prev && styles['pagination-link-prev'],
					props.next && styles['pagination-link-next'],
					props.isActive && styles['pagination-link-active'],
					props.disabled && styles['pagination-link-disabled'],
					styles['pagination-link-ellipsis'],
				)}
			>
				...
			</span>
		</li>
	);
};

const Pagination = ({ currentPage, lastPage, shallow = true }) => {
	const router = useRouter();

	let pages = [];
	const maxNumberOfPages = 7;
	const current = +currentPage;
	const prevPage = current - 1;
	const pagesTotal = +lastPage;

	const href = router.pathname;
	const asPath = router.asPath.split('?')[0];
	const notNextjsQueries = Object.keys(router.query).reduce((acc, key) => {
		if (
			router.pathname.indexOf(`[${key}]`) === -1 &&
			router.pathname.indexOf(`[...${key}]`) === -1
		) {
			acc[key] = router.query[key];
		}
		return acc;
	}, {});
	delete notNextjsQueries.page;

	const page = page => {
		return (
			<PaginationItem
				href={{
					pathname: href,
					query: page === 1 ? { ...notNextjsQueries } : { ...notNextjsQueries, page },
				}}
				asPath={{
					pathname: asPath,
					query: page === 1 ? { ...notNextjsQueries } : { ...notNextjsQueries, page },
				}}
				key={page}
				isActive={current === page}
				shallow={shallow}
			>
				{page}
			</PaginationItem>
		);
	};

	/* <- 1 2 3 4 5 6 7 -> */
	if (pagesTotal <= maxNumberOfPages) {
		for (let i = 1; i <= pagesTotal; i++) {
			pages.push(page(i));
		}
	} else {
		/* <- 1 2 3 4' 5 ... N -> */
		if (current < 5) {
			for (let i = 1; i <= 5; i++) {
				pages.push(page(i));
			}
			pages.push(<PaginationEllipsis key='rest2' />);
			pages.push(page(pagesTotal));
		} else if (current > pagesTotal - 4) {
			/* <- 1 ... N-4 N-3 N-2 N-1 N -> */
			pages.push(page(1));

			pages.push(<PaginationEllipsis key='rest' />);

			for (let i = pagesTotal - 4; i <= pagesTotal; i++) {
				pages.push(page(i));
			}
		} else {
			/* <- 1 ... 4 5' 6 ... N -> */
			pages = [].concat(
				page(1),

				<PaginationEllipsis key='rest' />,

				page(prevPage),
				page(current),
				page(current + 1),

				<PaginationEllipsis key='rest2' />,

				page(pagesTotal),
			);
		}
	}

	return (
		<ul className={styles.pagination}>
			<PaginationItem
				href={{
					pathname: href,
					query: prevPage === 1 ? { ...notNextjsQueries } : { ...notNextjsQueries, page: prevPage },
				}}
				asPath={{
					pathname: asPath,
					query: prevPage === 1 ? { ...notNextjsQueries } : { ...notNextjsQueries, page: prevPage },
				}}
				key='prev'
				prev
				disabled={current === 1}
				shallow={shallow}
			>
				Назад
			</PaginationItem>

			{pages}

			<PaginationItem
				href={{ pathname: href, query: { ...notNextjsQueries, page: current + 1 } }}
				asPath={{ pathname: asPath, query: { ...notNextjsQueries, page: current + 1 } }}
				key='next'
				next
				disabled={current === pagesTotal}
				shallow={shallow}
			>
				Вперед
			</PaginationItem>
		</ul>
	);
};

export default Pagination;
